import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import TaskModal from '../components/TaskModal'
import { useApp } from '../context/AppContext'

export default function Tidsplan() {
  const { eventId } = useParams()
  const { events, tasks } = useApp()
  const [view, setView] = useState('week')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedTask, setSelectedTask] = useState(null)

  const event = events && events.find(e => e.id === eventId)
  const eventTasks = tasks.filter(t => t.eventId === eventId && t.deadline).sort((a, b) => {
    const dateA = new Date(a.deadline)
    const dateB = new Date(b.deadline)
    if (dateA.getTime() !== dateB.getTime()) return dateA - dateB
    if (a.deadlineTime && b.deadlineTime) return a.deadlineTime.localeCompare(b.deadlineTime)
    return 0
  })

  const getTaskColor = (priority) => {
    switch (priority) {
      case 'high': return { bg: '#ffe4e4', text: '#cc2020' }
      case 'medium': return { bg: '#fff3e0', text: '#9a5800' }
      case 'low': return { bg: '#e8fdf5', text: '#007a58' }
      default: return { bg: '#f7f5ff', text: '#6c63ff' }
    }
  }

  const formatDateRange = (date, view) => {
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }
    if (view === 'week') {
      const startDate = new Date(date)
      startDate.setDate(startDate.getDate() - startDate.getDay() + 1)
      const endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + 6)
      return `${startDate.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })}  ${endDate.toLocaleDateString('da-DK', options)}`
    } else if (view === 'month') {
      return date.toLocaleDateString('da-DK', { year: 'numeric', month: 'long' })
    } else {
      return date.toLocaleDateString('da-DK', options)
    }
  }

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const getWeekDays = (date) => {
    const startDate = new Date(date)
    startDate.setDate(startDate.getDate() - startDate.getDay() + 1)
    const days = []
    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate)
      d.setDate(d.getDate() + i)
      days.push(d)
    }
    return days
  }

  const getTasksForDay = (date) => {
    return eventTasks.filter(t => {
      const taskDate = new Date(t.deadline)
      return taskDate.toDateString() === date.toDateString()
    })
  }

  const handlePrevious = () => {
    const newDate = new Date(currentDate)
    if (view === 'week') newDate.setDate(newDate.getDate() - 7)
    else if (view === 'month') newDate.setMonth(newDate.getMonth() - 1)
    else newDate.setDate(newDate.getDate() - 1)
    setCurrentDate(newDate)
  }

  const handleNext = () => {
    const newDate = new Date(currentDate)
    if (view === 'week') newDate.setDate(newDate.getDate() + 7)
    else if (view === 'month') newDate.setMonth(newDate.getMonth() + 1)
    else newDate.setDate(newDate.getDate() + 1)
    setCurrentDate(newDate)
  }

  const renderWeekView = () => {
    const days = getWeekDays(currentDate)
    const dayNames = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lr', 'Sn']
    const isMobile = window.innerWidth < 768
    const eventDate = new Date(event?.date)
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? 4 : 8 }}>
        {days.map((day, idx) => {
          const dayTasks = getTasksForDay(day)
          const isToday = day.toDateString() === new Date().toDateString()
          const isEventDay = day.toDateString() === eventDate.toDateString()
          return (
            <div key={idx} style={{
              border: isToday ? '2px solid #6c63ff' : '1px solid #e0e0e0',
              borderRadius: isMobile ? 6 : 12,
              padding: isMobile ? 6 : 12,
              minHeight: isMobile ? 300 : 400,
              background: isEventDay ? '#fffce8' : isToday ? '#f9f7ff' : '#fff'
            }}>
              <div style={{ fontSize: isMobile ? 9 : 11, fontWeight: 600, color: '#DF83A9', marginBottom: isMobile ? 4 : 8 }}>
                {dayNames[idx]} {day.getDate()}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 3 : 6 }}>
                {dayTasks.map(task => {
                  const color = getTaskColor(task.priority)
                  return (
                    <div
                      key={task.id}
                      onClick={() => setSelectedTask(task)}
                      style={{
                        background: color.bg,
                        border: `1px solid ${color.text}33`,
                        borderRadius: isMobile ? 4 : 8,
                        padding: isMobile ? '4px 6px' : '8px 10px',
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                    >
                      <div style={{ fontSize: isMobile ? 9 : 12, fontWeight: 600, color: color.text, marginBottom: 1 }}>
                        {task.deadlineTime ? `${task.deadlineTime} ` : ''}{task.title.slice(0, isMobile ? 12 : 20)}
                      </div>
                      <div style={{ fontSize: isMobile ? 8 : 10, color: color.text, opacity: 0.7 }}>
                        {task.assignedTo}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderMonthView = () => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const daysInMonth = getDaysInMonth(currentDate)
    const firstDay = getFirstDayOfMonth(currentDate)
    const dayNames = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lr', 'Sn']
    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1
    const totalCells = adjustedFirstDay + daysInMonth
    const weeks = Math.ceil(totalCells / 7)
    const cells = []
    const isMobile = window.innerWidth < 768
    const eventDate = new Date(event?.date)

    for (let i = 0; i < adjustedFirstDay; i++) {
      cells.push(null)
    }
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push(new Date(year, month, i))
    }

    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? 4 : 8, marginBottom: isMobile ? 4 : 8 }}>
          {dayNames.map(name => (
            <div key={name} style={{ textAlign: 'center', fontSize: isMobile ? 10 : 12, fontWeight: 600, color: '#DF83A9', padding: isMobile ? 4 : 8 }}>
              {name}
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? 4 : 8 }}>
          {cells.map((date, idx) => {
            if (!date) return <div key={`empty-${idx}`} />
            const dayTasks = getTasksForDay(date)
            const isToday = date.toDateString() === new Date().toDateString()
            const isEventDay = date.toDateString() === eventDate.toDateString()
            return (
              <div key={date.getDate()} style={{
                border: isToday ? '2px solid #6c63ff' : '1px solid #e0e0e0',
                borderRadius: isMobile ? 6 : 12,
                padding: isMobile ? 6 : 10,
                minHeight: isMobile ? 80 : 120,
                background: isEventDay ? '#fffce8' : isToday ? '#f9f7ff' : '#fff'
              }}>
                <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 600, color: '#1a1a2e', marginBottom: isMobile ? 4 : 8 }}>
                  {date.getDate()}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {dayTasks.slice(0, 3).map(task => {
                    const color = getTaskColor(task.priority)
                    return (
                      <div
                        key={task.id}
                        onClick={() => setSelectedTask(task)}
                        style={{
                          background: color.bg,
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          cursor: 'pointer',
                          border: `1px solid ${color.text}`,
                          fontSize: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          color: color.text
                        }}
                        title={task.title}
                      />
                    )
                  })}
                  {dayTasks.length > 3 && (
                    <div style={{ fontSize: 10, color: '#DF83A9', alignSelf: 'center' }}>
                      +{dayTasks.length - 3}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderDayView = () => {
    const dayTasks = getTasksForDay(currentDate)
    const dayNames = ['Sndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lrdag']
    const hours = Array.from({ length: 24 }, (_, i) => i)
    const eventDate = new Date(event?.date)
    const isEventDay = currentDate.toDateString() === eventDate.toDateString()

    return (
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 16 }}>
          {dayNames[currentDate.getDay()]} {currentDate.getDate()}. {currentDate.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden', background: isEventDay ? '#fffce8' : '#fff' }}>
          {hours.map(hour => {
            const hour2d = hour.toString().padStart(2, '0')
            const hourTasks = dayTasks.filter(t => t.deadlineTime && t.deadlineTime.startsWith(hour2d))
            return (
              <div key={hour} style={{ display: 'flex', borderBottom: '1px solid #e0e0e0', minHeight: 80 }}>
                <div style={{ width: 60, background: '#f7f5ff', padding: 12, fontSize: 12, fontWeight: 600, color: '#6c63ff', borderRight: '1px solid #e0e0e0' }}>
                  {hour2d}:00
                </div>
                <div style={{ flex: 1, padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {hourTasks.map(task => {
                    const color = getTaskColor(task.priority)
                    return (
                      <div
                        key={task.id}
                        onClick={() => setSelectedTask(task)}
                        style={{
                          background: color.bg,
                          border: `1px solid ${color.text}33`,
                          borderRadius: 8,
                          padding: '8px 10px',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 600, color: color.text }}>
                          {task.deadlineTime} {task.title}
                        </div>
                        <div style={{ fontSize: 10, color: color.text, opacity: 0.7 }}>
                          {task.assignedTo}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event?.name}</h1>
            <span className="page-subtitle">Tidsplan</span>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <button
              onClick={handlePrevious}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: '1px solid #e0e0e0',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {String.fromCharCode(60)}
            </button>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', minWidth: 180, textAlign: 'center' }}>
              {formatDateRange(currentDate, view)}
            </div>
            <button
              onClick={handleNext}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: '1px solid #e0e0e0',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {String.fromCharCode(62)}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            {['day', 'week', 'month'].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`filter-pill ${view === v ? 'active' : ''}`}
              >
                {v === 'day' ? 'dag' : v === 'week' ? 'uge' : 'mned'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: 20 }}>
          {view === 'week' && renderWeekView()}
          {view === 'month' && renderMonthView()}
          {view === 'day' && renderDayView()}

          {eventTasks.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <p style={{ fontSize: 13, color: '#DF83A9' }}>ingen opgaver planlagt endnu</p>
            </div>
          )}
        </div>
      </div>

      {selectedTask && <TaskModal task={selectedTask} onClose={() => setSelectedTask(null)} />}
    </AppLayout>
  )
}







